import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { style } from './secondary-nav.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { NavigationItem } from 'components/discovery/navigation-menu/typing/navigation-types.js'
import { BaseUrlController } from 'components/shared/enterprise-domains/base-url-controller.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'

initializeIconsByRole({
  'csc-secondary-nav.all-products': { name: 'angle-right', style: 'light' },
  'csc-secondary-nav.has-children': { name: 'angle-right', style: 'light' },
})

@customElement('csc-secondary-nav')
export class CSCSecondaryNav extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  private urls = new BaseUrlController(this)

  @property({ type: Object })
  navigation!: NavigationItem

  @property({ type: Boolean, attribute: 'all-products-link' })
  allProductsLink = false

  @property({ type: Boolean, attribute: 'show-all-products-link-on-overflow' })
  showAllProductsLinkOnOverflow: boolean = false

  @property({ type: Number, attribute: 'max-children' })
  maxChildren: number = Infinity

  @property({ type: Boolean, attribute: 'enable-click-through' })
  enableClickThrough: boolean = false

  @property({ type: Boolean, attribute: 'include-cateogory-name' })
  includeCategoryName: boolean = false

  @property({ type: Boolean, attribute: 'show-more-icon' })
  showMoreIcon: boolean = false

  private handleMainHeadingClick(event: Event): void {
    if (this.enableClickThrough && this.navigation.childs && this.navigation.childs.length > 0) {
      event.preventDefault()
      this.dispatchEvent(new CustomEvent<NavigationItem>('category-selected', {
        detail: this.navigation,
        bubbles: true,
        composed: true,
      }))
    }
  }

  override render(): TemplateResult {
    const parentPath = this.urls.path(`${this.navigation.slug}/${this.navigation.id}/`)

    const hasChildren = this.navigation.childs && this.navigation.childs.length > 0

    const heading = !(this.enableClickThrough && hasChildren)
      ? html`
        <cse-link
          variant="secondary"
          font-variant="${this.enableClickThrough ? 'body' : 'heading-5'}"
          url="${parentPath}"
        >
          ${this.navigation.name}
          ${hasChildren && this.showMoreIcon ? html`<cse-icon icon-role="csc-secondary-nav.has-children" slot="suffix"></cse-icon>` : nothing}
        </cse-link>
      `
      : nothing

    const headingClickThrough = this.enableClickThrough && hasChildren
      ? html`
        <cse-link
          variant="secondary"
          @click="${this.handleMainHeadingClick}"
          url="${parentPath}"
          class="main-heading-click-through"
        >
          ${this.navigation.name}
          ${this.showMoreIcon ? html`<cse-icon icon-role="csc-secondary-nav.has-children" slot="suffix"></cse-icon>` : nothing}
        </cse-link>
      `
      : nothing

    const childrenCount = this.navigation.childs ? this.navigation.childs.length : 0

    const shouldShowOverflowLink = this.showAllProductsLinkOnOverflow && (childrenCount > this.maxChildren)
    const shouldShowLink = this.allProductsLink || shouldShowOverflowLink

    const childLinksContent = !this.enableClickThrough && hasChildren && this.navigation.childs ?
      this.navigation.childs
        .slice(0, this.maxChildren === Infinity ? this.navigation.childs.length : this.maxChildren)
        .map(child => {
          const childPath = this.urls.path(`${child.slug}/${child.id}/`)
          const childHasChildren = child.childs && child.childs.length > 0
          return html`
            <cse-link variant="secondary" url="${childPath}">
              ${child.name}
              ${childHasChildren && this.showMoreIcon ? html`<cse-icon icon-role="csc-secondary-nav.has-children" slot="suffix"></cse-icon>` : nothing}
            </cse-link>
          `
        })
      : nothing

    const shouldRenderMenuItemList = headingClickThrough !== nothing || childLinksContent !== nothing

    return html`
      <div class="secondary-nav">
        ${heading}
        ${shouldRenderMenuItemList ? html`
          <div class="menu-item-list">
            ${headingClickThrough}
            ${childLinksContent}
          </div>
        ` : nothing}
        ${shouldShowLink ? html`
          <cse-link variant="primary" url="${parentPath}" class="all-products-link">
            ${t('all_products', { ns: 'csc-secondary-nav', default: 'See all products' })}
            ${this.includeCategoryName ? this.navigation.name : nothing }
            <cse-icon icon-role="csc-secondary-nav.all-products" slot="suffix"></cse-icon>
          </cse-link>
        ` : nothing }
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-secondary-nav': CSCSecondaryNav
  }
}
